/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { parse, isAfter, isEqual } from 'date-fns';
import { Voucher, MapVouchersToCardProps } from '../../types';

/**
 * Parses voucher expiry date string (e.g. "12 January 2026")
 * and normalizes it to start of day in UTC.
 *
 * Business rule:
 * - Voucher expiry is evaluated globally (UTC),
 *   not based on client local timezone.
 */
export const parseExpiryDateUTC = (expiryDate?: string): Date | null => {
  if (!expiryDate) return null;

  const parsed = parse(expiryDate, 'd MMMM yyyy', new Date());

  if (isNaN(parsed.getTime())) return null;

  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
  );
};

/**
 * Filters only active (non-expired) vouchers.
 * Expiry comparison is done in UTC to ensure
 * consistent behavior across all timezones.
 */
export const getValidVouchers = (vouchers: Voucher[] = []): Voucher[] => {
  const now = new Date();
  const todayUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );

  return vouchers.filter((voucher) => {
    if (!voucher.expiryDate) return true;

    const expiryUTC = parseExpiryDateUTC(voucher.expiryDate);
    if (!expiryUTC) return false;

    return isAfter(expiryUTC, todayUTC) || isEqual(expiryUTC, todayUTC);
  });
};

/**
 * Maps voucher data to UI card properties.
 * UI shape intentionally kept local to the widget.
 */
export const mapVouchersToCardProps = ({
  vouchers,
  claimedIds,
  imageUrl,
  screenWidth,
  imageWidth,
  imageHeight,
  onCTAPress,
  t,
}: MapVouchersToCardProps) => {
  const claimedIdSet = new Set(claimedIds);
  const cardWidth = screenWidth / 1.5;

  return vouchers.map((voucher, index) => {
    const isClaimed = claimedIdSet.has(voucher.promotionName);

    return {
      testID: `voucher-card-${voucher.promotionName}`,
      hasOutlineBorder: true,
      favouriteIcon: false,
      imageProps: {
        image: imageUrl,
        cacheProps: {
          priority: 'normal',
          cachePolicy: 'immutable',
        },
        height: imageHeight,
        width: imageWidth,
      },
      contentDetailsPropsValues: {
        width: cardWidth,
        imageWidth,
        primaryText: voucher.promotionName,
        secondaryText: t('searchHotelPage.voucherCarousel.description', {
          description: voucher.description,
          expiryDate: voucher.expiryDate,
        }),
        ctaText: isClaimed
          ? t('searchHotelPage.voucherCarousel.claimedCtaText')
          : t('searchHotelPage.voucherCarousel.claimCtaText'),
        onCTAPress: () => onCTAPress(voucher.promotionName, isClaimed, index),
      },
      width: cardWidth,
      imageHeight,
      imageWidth,
    };
  });
};
